import { Injectable } from '@angular/core';
import { findProductVariation } from '@gelato-api-ui/core/sanity/helpers/findProductVariation';
import { SanityFaqItem } from '@gelato-api-ui/core/sanity/sanity-faq-item';
import { SanityPrintAreas } from '@gelato-api-ui/core/sanity/sanity-print-areas';
import { SanityProduct } from '@gelato-api-ui/core/sanity/sanity-product';
import { SanityProductCareInstructionGroup } from '@gelato-api-ui/core/sanity/sanity-product-care-instruction-group';
import { SanityProductControl, SanityProductControlKey } from '@gelato-api-ui/core/sanity/sanity-product-control';
import { SanityProductFulfillment } from '@gelato-api-ui/core/sanity/sanity-product-fulfillment';
import { SanityProductPackaging } from '@gelato-api-ui/core/sanity/sanity-product-packaging';
import { SanityProductPages } from '@gelato-api-ui/core/sanity/sanity-product-pages';
import { SanityProductVariantFrameAssembling } from '@gelato-api-ui/core/sanity/sanity-product-variant';
import { SanityProductVariation } from '@gelato-api-ui/core/sanity/sanity-product-variation';
import { SanityProductVariationSpecificContentItem } from '@gelato-api-ui/core/sanity/sanity-product-variation-specific-content-item';
import { SanityMatchingProductControlsService } from '@gelato-api-ui/core/sanity/services/sanity-matching-product-controls.service';
import { GarmentTechnology } from '@gelato-api-ui/core/sanity/types/garment-technology.enum';
import { SanityContentAvailabilityService } from '@product-catalogue/src/lib/product-details-shared/services/sanity-content-availability.service';

@Injectable({ providedIn: 'root' })
export class SanityProductDetailsService {
  constructor(
    private readonly contentAvailability: SanityContentAvailabilityService,
    private readonly sanityMatchingProductControlsService: SanityMatchingProductControlsService,
  ) {}

  getDescription(product: SanityProduct, selectedProductVariation: SanityProductVariation): string {
    const variationSpecificContent = this.getProductVariationSpecificContentItem(product, selectedProductVariation);

    return variationSpecificContent?.description || product?.description;
  }

  getAvailableProductControlsForCurrentVariation(
    product: SanityProduct,
    selectedProductVariation: SanityProductVariation,
  ): SanityProductControl[] {
    const controls = this.sanityMatchingProductControlsService.getAvailableProductControlsForCurrentVariation(
      product?.productControls,
      product?.productVariations,
      selectedProductVariation,
    );

    return this.modifyControlsForNonStandardCases(selectedProductVariation, controls);
  }

  getProductPages(product: SanityProduct): SanityProductPages {
    return product?.pages;
  }

  getFulfilment(product: SanityProduct, selectedProductVariation: SanityProductVariation): SanityProductFulfillment {
    const variationSpecificContent = this.getProductVariationSpecificContentItem(product, selectedProductVariation);

    return variationSpecificContent?.fulfilment || product?.fulfilment;
  }

  getFulfillmentDescription(product: SanityProduct, selectedProductVariation: SanityProductVariation): string {
    return this.getFulfilment(product, selectedProductVariation)?.description;
  }

  getFulfillmentCountriesCount(product: SanityProduct, selectedProductVariation: SanityProductVariation): number {
    return this.getFulfillmentCountriesList(product, selectedProductVariation)?.length;
  }

  getProductCareInstructions(product: SanityProduct): SanityProductCareInstructionGroup[] {
    return product?.productCareInstructions?.instructionGroups;
  }

  getFaq(product: SanityProduct, selectedProductVariation: SanityProductVariation): SanityFaqItem[] {
    const variationSpecificContent = this.getProductVariationSpecificContentItem(product, selectedProductVariation);

    return variationSpecificContent?.faq || product?.faq;
  }

  getPackaging(product: SanityProduct, selectedProductVariation: SanityProductVariation): SanityProductPackaging {
    const variationSpecificContent = this.getProductVariationSpecificContentItem(product, selectedProductVariation);

    return variationSpecificContent?.packaging || product?.packaging;
  }

  getPrintAreas(product: SanityProduct, selectedProductVariation: SanityProductVariation): SanityPrintAreas {
    return product?.printAreas.find(printArea =>
      this.contentAvailability.isProductControlOptionSelected(
        printArea.productControlOptions,
        selectedProductVariation,
      ),
    );
  }

  hasMountedFramedPosters(product: SanityProduct): boolean {
    return Boolean(
      findProductVariation(product?.productVariations, {
        frameassembling: SanityProductVariantFrameAssembling.mounted,
      }),
    );
  }

  private getFulfillmentCountriesList(
    product: SanityProduct,
    selectedProductVariation: SanityProductVariation,
  ): string[] {
    const countriesListString = this.getFulfilment(product, selectedProductVariation)?.countriesList;

    if (countriesListString?.length <= 1) {
      return null;
    }

    return countriesListString.split(',');
  }

  private getProductVariationSpecificContentItem(
    product: SanityProduct,
    selectedProductVariation: SanityProductVariation,
  ): SanityProductVariationSpecificContentItem {
    return (product?.productVariationSpecificContent || []).find(
      (loopItem: SanityProductVariationSpecificContentItem): boolean => {
        if (!loopItem?.productControlKey || !loopItem?.productOptionValue) {
          return false;
        }

        const key: SanityProductControlKey = loopItem?.productControlKey;
        const value: string = loopItem?.productOptionValue;

        return selectedProductVariation[key] === value;
      },
    );
  }

  /**
   * For some products the default logic for disabling controls does not work.
   * This method is used to modify the controls in such cases.
   */
  private modifyControlsForNonStandardCases(
    selectedProductVariation: SanityProductVariation,
    controls: SanityProductControl[],
  ): SanityProductControl[] {
    // this controls should never be disabled
    const neverDisabledControlsKeys = [SanityProductControlKey.garmentTechnology, SanityProductControlKey.phoneBrand];

    // there is different behaviour if embroidery control is selected
    const isEmbroiderySelected = selectedProductVariation.garmenttechnology === GarmentTechnology.Embroidery;

    return controls.map(control => {
      let updatedOptions = control.options;

      // enable all options for controls that should never be disabled
      if (neverDisabledControlsKeys.includes(control.key)) {
        updatedOptions = control.options.map(option => {
          return {
            ...option,
            disabled: false,
          };
        });
      }

      // remove disabled controls options if embroidery is selected
      if (isEmbroiderySelected && control.key !== SanityProductControlKey.garmentTechnology) {
        updatedOptions = control.options.filter(option => !option.disabled);
      }

      // return sorted options: enabled options first, disabled options last
      return {
        ...control,
        options: updatedOptions.sort((a, b) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1)),
      };
    });
  }
}
